import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import { apiFetch } from '../utils/auth';
import { useRoleState } from '../context/RoleContext';
import adminStyles from './AdminPanel.module.css';
import newsStyles from './News.module.css';
import styles from './Battles.module.css';
import { Input, SearchInput, ActionsMenu, ActionsMenuDotsIcon, Modal, EmployeeTaskStatsAvatar, Avatar } from './UI';
import { useEmployeeTaskStats } from '../utils/employeeTaskStats';
import { ReactComponent as FabBubble } from '../icons/circle.svg';
import { ReactComponent as AddIconPlus } from '../icons/add_icon_plus.svg';
import { ReactComponent as UploadChatPictureIcon } from '../icons/upload_chat_picture.svg';

const API_ORIGIN = (() => {
  try { return API_URL.replace(/\/api\/v\d+\/?$/i, ''); } catch { return ''; }
})();

function resolveImage(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${API_ORIGIN}${url}`;
  return url;
}

function normalizeOverview(payload) {
  const eventSource = payload?.event || payload?.main_event || payload?.settings || {};
  const teamsSource = Array.isArray(payload?.teams) ? payload.teams : [];

  const teams = teamsSource.map((team) => {
    const membersRaw = Array.isArray(team?.members)
      ? team.members
      : Array.isArray(team?.participants)
        ? team.participants
        : Array.isArray(team?.employees)
          ? team.employees
          : [];

    const members = membersRaw.map((member) => {
      const memberId = Number(member?.id ?? member?.employee_id ?? member?.user_id ?? 0);
      return {
        id: Number.isFinite(memberId) ? memberId : 0,
        name: String(member?.name || member?.employee_name || member?.full_name || 'Без имени'),
        avatar: member?.avatar || member?.avatar_url || member?.image_url || '',
        points: Number(member?.battle_points ?? member?.points ?? member?.score ?? 0) || 0,
      };
    });

    const captainId = Number(team?.captain_id ?? team?.captain?.id ?? 0);
    const captainName = String(
      team?.captain?.name
      || team?.captain_name
      || members.find((m) => m.id === captainId)?.name
      || ''
    );

    return {
      id: Number(team?.id ?? 0) || 0,
      name: String(team?.name || team?.title || `Команда #${team?.id ?? '—'}`),
      image: team?.image_url || team?.image || team?.logo || '',
      captainId: Number.isFinite(captainId) ? captainId : 0,
      captainName,
      totalPoints: Number(team?.total_points ?? team?.battle_points ?? 0) || members.reduce((acc, x) => acc + (Number(x.points) || 0), 0),
      members,
    };
  });

  teams.sort((a, b) => b.totalPoints - a.totalPoints || a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }));

  return {
    event: {
      isActual: !!eventSource?.is_actual,
      title: String(eventSource?.title || 'Сражения'),
      description: String(eventSource?.description || ''),
    },
    teams,
  };
}

function getTeamTabLabel(team, fallbackIndex) {
  const name = String(team?.name || '').trim();
  if (name) return name;
  const teamId = Number(team?.id);
  if (Number.isFinite(teamId) && teamId > 0) return `Команда ${teamId}`;
  return `Команда ${fallbackIndex + 1}`;
}

const TEAM_TAB_COMPACT_MAX_LABEL_LEN = 7;

export default function Battles() {
  const { role } = useRoleState();
  const isAdmin = String(role || '').toLowerCase() === 'admin';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState({ event: { isActual: false, title: '', description: '' }, teams: [] });
  const [activeTeamId, setActiveTeamId] = useState(null);
  const [memberMenuOpenId, setMemberMenuOpenId] = useState(null);
  const [activeMemberId, setActiveMemberId] = useState(null);
  const [activeMemberInfo, setActiveMemberInfo] = useState(null);
  const [activeMemberScores, setActiveMemberScores] = useState([]);
  const [memberDetailsLoading, setMemberDetailsLoading] = useState(false);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [scoreMember, setScoreMember] = useState(null);
  const [scoreItems, setScoreItems] = useState([]);
  const [deletedCategoryIds, setDeletedCategoryIds] = useState([]);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreSaving, setScoreSaving] = useState(false);
  const [categoryBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createCaptainSearch, setCreateCaptainSearch] = useState('');
  const [createCaptainResults, setCreateCaptainResults] = useState([]);
  const [createCaptain, setCreateCaptain] = useState(null);
  const [createImageFile, setCreateImageFile] = useState(null);
  const [createImagePreview, setCreateImagePreview] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [hideScoreInputClear, setHideScoreInputClear] = useState(false);

  useEffect(() => {
    const query = String(createCaptainSearch || '').trim();
    if (!createOpen || !query || createCaptain) {
      setCreateCaptainResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await apiFetch(`${API_URL}/employees/search/?query=${encodeURIComponent(query)}`);
        const data = res.ok ? await res.json() : [];
        const arr = Array.isArray(data) ? data : [];
        setCreateCaptainResults(arr);
      } catch {
        setCreateCaptainResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [createOpen, createCaptainSearch, createCaptain]);

  useEffect(() => {
    if (!createImageFile) {
      setCreateImagePreview('');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(createImageFile);
    setCreateImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [createImageFile]);

  useEffect(() => {
    if (!scoreModalOpen || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 380px)');
    const sync = () => setHideScoreInputClear(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [scoreModalOpen]);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`${API_URL}/battles/overview`);
      if (!res.ok) throw new Error('Не удалось загрузить данные сражений');
      const data = await res.json();
      const normalized = normalizeOverview(data || {});
      setOverview(normalized);
      setActiveTeamId((prev) => {
        if (prev == null) return null;
        return normalized.teams.some((t) => t.id === prev) ? prev : null;
      });
    } catch (e) {
      setError(e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const rankedTeams = useMemo(() => overview.teams.map((team, index) => ({ ...team, place: index + 1 })), [overview.teams]);
  const activeTeam = useMemo(
    () => (activeTeamId == null ? null : rankedTeams.find((x) => x.id === activeTeamId) || null),
    [rankedTeams, activeTeamId]
  );

  const teamTabsCompactMobile = useMemo(() => {
    if (!rankedTeams.length) return false;
    return rankedTeams.every(
      (team, index) => getTeamTabLabel(team, index).length <= TEAM_TAB_COMPACT_MAX_LABEL_LEN
    );
  }, [rankedTeams]);

  const battleEmployeeIds = useMemo(() => {
    const ids = new Set();
    for (const team of overview.teams) {
      for (const member of team.members || []) {
        if (member.id > 0) ids.add(member.id);
      }
    }
    if (Number(activeMemberId) > 0) ids.add(Number(activeMemberId));
    if (createCaptain?.id) ids.add(Number(createCaptain.id));
    for (const emp of createCaptainResults) {
      const id = Number(emp.id);
      if (id > 0) ids.add(id);
    }
    return [...ids];
  }, [overview.teams, activeMemberId, createCaptain, createCaptainResults]);

  const employeeTaskStatsMap = useEmployeeTaskStats(battleEmployeeIds);

  const openTeam = (teamId) => {
    setActiveTeamId(teamId);
  };

  const closeTeam = () => {
    setActiveTeamId(null);
    setMemberMenuOpenId(null);
    setActiveMemberId(null);
    setActiveMemberInfo(null);
    setActiveMemberScores([]);
  };

  const normalizeScoreList = (payload) => {
    const src = Array.isArray(payload) ? payload : (Array.isArray(payload?.items) ? payload.items : (Array.isArray(payload?.scores) ? payload.scores : []));
    return src
      .map((x, idx) => ({
        categoryId: Number(x?.category_id ?? x?.id ?? idx + 1),
        categoryName: String(x?.name || x?.category_name || x?.title || `Категория ${idx + 1}`),
        value: Number(x?.value ?? x?.points ?? x?.score ?? 0) || 0,
        order: Number(x?.sort_order ?? idx + 1) || (idx + 1),
      }))
      .sort((a, b) => a.order - b.order || a.categoryName.localeCompare(b.categoryName, 'ru', { sensitivity: 'base' }));
  };

  const openMemberDetails = async (member) => {
    setActiveMemberId(member.id);
    setMemberDetailsLoading(true);
    try {
      const [empRes, scoreRes] = await Promise.all([
        apiFetch(`${API_URL}/employees/${member.id}`),
        apiFetch(`${API_URL}/employees/${member.id}/battle-scores`),
      ]);
      const empData = empRes.ok ? await empRes.json() : null;
      const scoreData = scoreRes.ok ? await scoreRes.json() : [];
      setActiveMemberInfo({
        id: member.id,
        name: member.name,
        avatar: member.avatar,
        position: empData?.position || '',
      });
      setActiveMemberScores(normalizeScoreList(scoreData));
    } catch {
      setActiveMemberInfo({
        id: member.id,
        name: member.name,
        avatar: member.avatar,
        position: '',
      });
      setActiveMemberScores([]);
    } finally {
      setMemberDetailsLoading(false);
    }
  };

  const closeMemberDetails = () => {
    setActiveMemberId(null);
    setActiveMemberInfo(null);
    setActiveMemberScores([]);
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (!memberMenuOpenId) return;
      if (e.target?.closest?.(`.${styles.memberMenuWrap}`)) return;
      setMemberMenuOpenId(null);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setMemberMenuOpenId(null);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [memberMenuOpenId]);

  const normalizeCategories = (payload) => {
    const src = Array.isArray(payload) ? payload : (Array.isArray(payload?.items) ? payload.items : (Array.isArray(payload?.categories) ? payload.categories : []));
    return src
      .map((x) => ({
        id: Number(x?.id ?? x?.category_id ?? 0),
        name: String(x?.name || x?.title || x?.label || 'Категория'),
      }))
      .filter((x) => Number.isFinite(x.id) && x.id > 0);
  };

  const normalizeEmployeeScores = (payload) => {
    const src = Array.isArray(payload) ? payload : (Array.isArray(payload?.items) ? payload.items : (Array.isArray(payload?.scores) ? payload.scores : []));
    const map = new Map();
    src.forEach((x) => {
      const categoryId = Number(x?.category_id ?? x?.id ?? 0);
      if (!Number.isFinite(categoryId) || categoryId <= 0) return;
      const points = Number(x?.points ?? x?.score ?? x?.value ?? 0) || 0;
      map.set(categoryId, points);
    });
    return map;
  };

  const fetchJsonWithFallback = async (urls) => {
    let lastError = null;
    for (const url of urls) {
      try {
        const res = await apiFetch(url);
        if (!res.ok) {
          lastError = new Error(`HTTP ${res.status} for ${url}`);
          continue;
        }
        return await res.json();
      } catch (e) {
        lastError = e;
      }
    }
    throw lastError || new Error('Не удалось загрузить данные');
  };

  const loadScoreEditorData = useCallback(async (memberId) => {
    setScoreLoading(true);
    try {
      const [catData, scoreData] = await Promise.all([
        fetchJsonWithFallback([
          `${API_URL}/battles/score-categories`,
          `${API_URL}/battles/categories`,
        ]),
        fetchJsonWithFallback([
          `${API_URL}/employees/${memberId}/battle-scores`,
        ]),
      ]);
      const categories = normalizeCategories(catData);
      const scoreMap = normalizeEmployeeScores(scoreData);
      setScoreItems(categories.map((c) => ({
        categoryId: c.id,
        clientId: `cat-${c.id}`,
        categoryName: c.name,
        originalCategoryName: c.name,
        points: String(scoreMap.get(c.id) ?? 0),
      })));
      setDeletedCategoryIds([]);
    } catch (e) {
      alert('Не удалось загрузить категории/очки. Проверьте, что backend-эндпоинты battles categories и employee battle-scores доступны.');
      setScoreItems([]);
    } finally {
      setScoreLoading(false);
    }
  }, []);

  const openScoreEditor = async (member) => {
    if (!isAdmin) return;
    setMemberMenuOpenId(null);
    setScoreMember(member);
    setScoreModalOpen(true);
    setDeletedCategoryIds([]);
    await loadScoreEditorData(member.id);
  };

  const closeScoreEditor = () => {
    if (scoreSaving) return;
    setScoreModalOpen(false);
    setScoreMember(null);
    setScoreItems([]);
    setDeletedCategoryIds([]);
  };

  const saveScoreEditor = async () => {
    if (!scoreMember || scoreSaving) return;
    setScoreSaving(true);
    try {
      for (const categoryId of deletedCategoryIds) {
        const res = await apiFetch(`${API_URL}/battles/score-categories/${categoryId}`, {
          method: 'DELETE',
        });
        if (!res.ok) throw new Error('Не удалось удалить категорию');
      }

      const preparedItems = [];
      for (const item of scoreItems) {
        const trimmedName = String(item.categoryName || '').trim();
        const numericValue = Number(item.points) || 0;
        if (!item.categoryId && !trimmedName && numericValue === 0) {
          continue;
        }
        if (!item.categoryId && !trimmedName) {
          throw new Error('Заполните название новой категории или удалите пустую строку');
        }

        if (item.categoryId) {
          preparedItems.push({
            categoryId: item.categoryId,
            oldName: String(item.originalCategoryName || '').trim(),
            newName: trimmedName || String(item.originalCategoryName || '').trim(),
            points: numericValue,
          });
          continue;
        }

        const createRes = await apiFetch(`${API_URL}/battles/score-categories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmedName }),
        });
        if (!createRes.ok) throw new Error(`Не удалось создать категорию "${trimmedName}"`);
        const createData = await createRes.json().catch(() => ({}));
        const newId = Number(
          createData?.id
          ?? createData?.category_id
          ?? createData?.data?.id
          ?? createData?.item?.id
          ?? 0
        );
        if (!Number.isFinite(newId) || newId <= 0) {
          throw new Error(`Категория "${trimmedName}" создана, но backend не вернул id`);
        }
        preparedItems.push({
          categoryId: newId,
          oldName: trimmedName,
          newName: trimmedName,
          points: numericValue,
        });
      }

      const renamed = preparedItems
        .map((x) => ({
          categoryId: x.categoryId,
          oldName: String(x.oldName || '').trim(),
          newName: String(x.newName || '').trim(),
        }))
        .filter((x) => x.newName && x.newName !== x.oldName);

      for (const item of renamed) {
        let updated = false;
        const patchRes = await apiFetch(`${API_URL}/battles/score-categories/${item.categoryId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: item.newName }),
        });
        if (patchRes.ok) {
          updated = true;
        }
        if (!updated) {
          const putRes = await apiFetch(`${API_URL}/battles/score-categories/${item.categoryId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: item.newName }),
          });
          if (!putRes.ok) throw new Error(`Не удалось переименовать категорию "${item.oldName}"`);
        }
      }

      const scores = preparedItems.map((x) => {
        return {
          category_id: x.categoryId,
          // backend ручки battle-scores обычно читают поле value
          value: Number(x.points) || 0,
          // оставляем points для совместимости со старыми реализациями
          points: Number(x.points) || 0,
        };
      });
      const res = await apiFetch(`${API_URL}/employees/${scoreMember.id}/battle-scores`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scores }),
      });
      if (!res.ok) throw new Error('Не удалось сохранить очки по категориям');
      closeScoreEditor();
      await loadOverview();
    } catch (e) {
      alert(e?.message || 'Не удалось сохранить очки');
    } finally {
      setScoreSaving(false);
    }
  };

  const addScoreCategoryRow = () => {
    if (scoreLoading || scoreSaving || categoryBusy) return;
    setScoreItems((prev) => ([
      ...prev,
      {
        categoryId: null,
        clientId: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        categoryName: '',
        originalCategoryName: '',
        points: '0',
      },
    ]));
  };

  const removeScoreCategoryRow = (item) => {
    if (scoreLoading || scoreSaving || categoryBusy) return;
    const rowKey = item.clientId || item.categoryId;
    setScoreItems((prev) => prev.filter((x) => (x.clientId || x.categoryId) !== rowKey));
    if (item.categoryId) {
      setDeletedCategoryIds((prev) => (prev.includes(item.categoryId) ? prev : [...prev, item.categoryId]));
    }
  };

  const deleteMember = async (memberId) => {
    setMemberMenuOpenId(null);
    if (!window.confirm('Удалить участника из команды?')) return;
    try {
      const res = await apiFetch(`${API_URL}/employees/${memberId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temporary_team_id: null }),
      });
      if (!res.ok) throw new Error('Не удалось удалить участника из команды');
      await loadOverview();
    } catch (e) {
      alert(e?.message || 'Не удалось удалить участника из команды');
    }
  };

  const openCreateTeam = () => {
    setCreateName('');
    setCreateCaptainSearch('');
    setCreateCaptainResults([]);
    setCreateCaptain(null);
    setCreateImageFile(null);
    setCreateImagePreview('');
    setCreateOpen(true);
  };

  const closeCreateTeam = () => {
    if (creatingTeam) return;
    setCreateOpen(false);
    setCreateName('');
    setCreateCaptainSearch('');
    setCreateCaptainResults([]);
    setCreateCaptain(null);
    setCreateImageFile(null);
    setCreateImagePreview('');
  };

  const createTemporaryTeam = async () => {
    const name = String(createName || '').trim();
    if (!name || creatingTeam) return;
    setCreatingTeam(true);
    try {
      const res = await apiFetch(`${API_URL}/structure/temporary-teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          captain_id: createCaptain ? Number(createCaptain.id) : null,
        }),
      });
      if (res.status === 409) {
        alert('Временная команда с таким названием уже существует');
        return;
      }
      if (!res.ok) throw new Error('Не удалось создать временную команду');
      const team = await res.json();
      if (createImageFile && team?.id) {
        const fd = new FormData();
        fd.append('image', createImageFile);
        const up = await apiFetch(`${API_URL}/structure/temporary-teams/${team.id}/image`, {
          method: 'POST',
          body: fd,
        });
        if (!up.ok) alert('Команда создана, но не удалось загрузить изображение');
      }
      setCreateOpen(false);
      setCreateName('');
      setCreateCaptainSearch('');
      setCreateCaptainResults([]);
      setCreateCaptain(null);
      setCreateImageFile(null);
      setCreateImagePreview('');
      await loadOverview();
    } catch (e) {
      alert(e?.message || 'Не удалось создать временную команду');
    } finally {
      setCreatingTeam(false);
    }
  };

  if (loading) return <div className={styles.state}>Загрузка сражений…</div>;
  if (error) return <div className={styles.error}>Ошибка: {error}</div>;

  return (
    <div className={isAdmin ? `${styles.page} ${styles.pageWithAdminFab}` : styles.page}>
      <section className={styles.eventHeader}>
        <h1 className={styles.eventTitle}>{overview.event.title || 'Сражения'}</h1>
        <div className={styles.eventLine} />
        {!!overview.event.description && <p className={styles.eventDescription}>{overview.event.description}</p>}
      </section>

      {!activeTeam && (
        <section className={styles.block}>
          <div className={styles.blockTitle}>Дистрикты</div>
          <ul className={styles.list}>
            {rankedTeams.map((team) => (
              <li
                key={team.id}
                className={styles.row}
                onClick={() => openTeam(team.id)}
              >
                <div className={`${styles.place} ${team.place === 1 ? styles.placeLeader : ''}`}>{team.place}</div>
                <Avatar src={resolveImage(team.image)} name={team.name} alt={team.name || ''} size={44} className={styles.avatar} />
                <div className={styles.main}>
                  <div className={`${styles.name} ${team.place === 1 ? styles.nameLeader : ''}`}>{team.name}</div>
                  <div className={styles.meta}>{team.captainName || 'Ментор не указан'}</div>
                </div>
                <div className={`${styles.points} ${team.place === 1 ? styles.pointsLeader : ''}`}>{team.totalPoints}</div>
              </li>
            ))}
            {rankedTeams.length === 0 && <li className={styles.empty}>Пока нет данных по сражениям</li>}
          </ul>
        </section>
      )}

      {activeTeam && !activeMemberId && (
        <section className={styles.block}>
          <div className={styles.breadcrumbsBar}>

            <div className={styles.breadcrumbs} aria-label="Хлебные крошки">
              <button type="button" className={styles.crumbBtn} onClick={closeTeam}>
                Дистрикты
              </button>
              <span className={styles.crumbSep}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </span>
              <span className={styles.crumbCurrent}>{activeTeam?.name || 'Команда'}</span>
            </div>
          </div>
          <div
            className={
              teamTabsCompactMobile
                ? `${styles.teamTabs} ${styles.teamTabsShortLabels}`
                : styles.teamTabs
            }
          >
            {rankedTeams.map((team, index) => (
              <button
                key={team.id}
                type="button"
                className={`${styles.teamTab} ${activeTeam?.id === team.id ? styles.teamTabActive : ''}`}
                onClick={() => openTeam(team.id)}
              >
                {getTeamTabLabel(team, index)}
              </button>
            ))}
          </div>

          <ul className={styles.list}>
            {(activeTeam?.members || []).map((member, idx) => {
              return (
                <li key={member.id} className={styles.row}>
                  <button
                    type="button"
                    className={styles.memberRowClick}
                    onClick={() => openMemberDetails(member)}
                    aria-label={`Открыть информацию о ${member.name}`}
                  >
                    <div className={`${styles.place} ${idx === 0 ? styles.placeLeader : ''}`}>{idx + 1}</div>
                    <EmployeeTaskStatsAvatar
                      employeeId={member.id}
                      stats={employeeTaskStatsMap.get(member.id)}
                      fetchStats={false}
                      avatar={resolveImage(member.avatar) || undefined}
                      size={44}
                      alt={member.name}
                    />
                    <div className={styles.main}>
                      <div className={`${styles.name} ${idx === 0 ? styles.nameLeader : ''}`}>{member.name}</div>
                      <div className={`${styles.pointsInline} ${idx === 0 ? styles.pointsInlineLeader : ''}`}>
                        {member.points}
                      </div>
                    </div>
                  </button>
                  {isAdmin && (
                    <ActionsMenu
                      open={memberMenuOpenId === member.id}
                      onToggle={(e) => {
                        e.stopPropagation();
                        setMemberMenuOpenId((cur) => (cur === member.id ? null : member.id));
                      }}
                      items={[
                        {
                          key: 'edit',
                          label: 'Редактировать',
                          onClick: () => openScoreEditor(member),
                        },
                        {
                          key: 'delete',
                          label: 'Удалить',
                          danger: true,
                          onClick: () => deleteMember(member.id),
                        },
                      ]}
                      title="Действия"
                      align="left"
                      trigger={<ActionsMenuDotsIcon />}
                      wrapClassName={styles.memberMenuWrap}
                      buttonClassName={`${styles.moreBtn} ${styles.memberMenuBtn}`}
                    />
                  )}
                </li>
              );
            })}
            {(activeTeam?.members || []).length === 0 && <li className={styles.empty}>В команде пока нет участников</li>}
          </ul>
        </section>
      )}

      {activeTeam && activeMemberId && (
        <section className={styles.block}>
          <div className={styles.breadcrumbsBar}>
            <div className={styles.breadcrumbs} aria-label="Хлебные крошки">
              <button
                type="button"
                className={styles.crumbBtn}
                onClick={() => {
                  closeMemberDetails();
                  closeTeam();
                }}
              >
                Дистрикты
              </button>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <button type="button" className={styles.crumbBtn} onClick={closeMemberDetails}>
                Участники
              </button>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span className={styles.crumbCurrent}>{activeMemberInfo?.name || 'Карточка сотрудника'}</span>
            </div>
          </div>
          {memberDetailsLoading ? (
            <div className={styles.state}>Загрузка информации об участнике...</div>
          ) : (
            <div className={styles.memberDetails}>
              <div className={styles.memberCard}>
                <EmployeeTaskStatsAvatar
                  employeeId={activeMemberInfo?.id}
                  stats={employeeTaskStatsMap.get(Number(activeMemberInfo?.id))}
                  fetchStats={false}
                  avatar={resolveImage(activeMemberInfo?.avatar) || undefined}
                  size={64}
                  alt={activeMemberInfo?.name || 'Сотрудник'}
                />
                <div className={styles.memberCardInfo}>
                  <div className={styles.memberCardName}>{activeMemberInfo?.name || 'Сотрудник'}</div>
                  {!!activeMemberInfo?.position && <div className={styles.memberCardMeta}>{activeMemberInfo.position}</div>}
                </div>
              </div>

              <div className={styles.memberScoresTitle}>Категории и очки</div>
              <ul className={styles.memberScoresList}>
                {activeMemberScores.map((x) => (
                  <li key={`${x.categoryId}-${x.categoryName}`} className={styles.memberScoresRow}>
                    <span>{x.categoryName}</span>
                    <strong>{x.value}</strong>
                  </li>
                ))}
                {activeMemberScores.length === 0 && (
                  <li className={styles.empty}>Для сотрудника пока нет очков по категориям</li>
                )}
              </ul>
              <div className={styles.memberScoresTotal}>
                {`Итого: ${activeMemberScores.reduce((acc, x) => acc + (Number(x.value) || 0), 0)}`}
              </div>
            </div>
          )}
        </section>
      )}

      {isAdmin && (
        <button
          className={newsStyles.newsFab}
          onClick={openCreateTeam}
          aria-label="Добавить временную команду"
          title="Добавить временную команду"
          type="button"
        >
          <span className={adminStyles.fabSvgWrap} aria-hidden="true">
            <FabBubble className={adminStyles.fabBubble} />
            <div className={adminStyles.fabInner}>
              <AddIconPlus className={adminStyles.fabIcon} />
            </div>
          </span>
        </button>
      )}

      {isAdmin && createOpen && (
        <Modal
          title="Новая временная команда"
          onClose={closeCreateTeam}
          onCancel={closeCreateTeam}
          cancelDisabled={creatingTeam}
          onSubmit={createTemporaryTeam}
          submitLabel={creatingTeam ? 'Создаем...' : 'Создать'}
          submitDisabled={!String(createName || '').trim() || creatingTeam}
          closeOnBackdrop={!creatingTeam}
          bodyClassName={styles.battlesModalBody}
        >
          <div className={styles.teamTopRow}>
            <div className={styles.teamImageCol}>
              <label
                title={createImagePreview ? 'Сменить изображение' : 'Загрузить изображение'}
                className={styles.imagePicker}
              >
                <input
                  type="file"
                  accept="image/*"
                  className={styles.hiddenInput}
                  onChange={(e) => setCreateImageFile(e.target.files?.[0] || null)}
                  disabled={creatingTeam}
                />
                {createImagePreview ? (
                  <img src={createImagePreview} alt="" className={styles.imageThumb} />
                ) : (
                  <div className={styles.imageEmptyState}>
                    <UploadChatPictureIcon className={styles.uploadIcon} />
                    <span className={styles.imageEmptyText}>Нажмите, чтобы загрузить</span>
                  </div>
                )}
              </label>
            </div>

            <div className={styles.teamMetaCol}>
              <label className={styles.battlesFieldLabel} htmlFor="battle-team-name">
                Название <span style={{ color: '#C01048', fontWeight: '400' }}>*</span>
              </label>
              <Input
                id="battle-team-name"
                className={adminStyles.fieldInput}
                placeholder="Например, Голодные игры 2026"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                ariaLabel="Название команды"
                disabled={creatingTeam}
              />

              <label className={styles.battlesFieldLabel}>Ментор (сотрудник)</label>
              {createCaptain ? (
                <div className={styles.selectedCaptain}>
                  <EmployeeTaskStatsAvatar
                    employeeId={createCaptain.id}
                    stats={employeeTaskStatsMap.get(Number(createCaptain.id))}
                    fetchStats={false}
                    avatar={resolveImage(createCaptain.avatar) || undefined}
                    size={24}
                    alt={createCaptain.name}
                  />
                  <span className={styles.selectedCaptainName}>{createCaptain.name}</span>
                  <button
                    type="button"
                    className={styles.clearCaptainBtn}
                    onClick={() => setCreateCaptain(null)}
                    disabled={creatingTeam}
                    aria-label="Убрать ментора"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className={styles.searchContainer}>
                  <SearchInput
                    className={adminStyles.fieldInput}
                    placeholder="Выбор руководителя"
                    value={createCaptainSearch}
                    onChange={(e) => setCreateCaptainSearch(e.target.value)}
                    ariaLabel="Поиск ментора"
                    disabled={creatingTeam}
                  />
                  {createCaptainResults.length > 0 && createCaptainSearch.trim().length > 0 && (
                    <ul className={styles.searchDropdown}>
                      {createCaptainResults.map((emp) => (
                        <li
                          key={emp.id}
                          className={styles.searchItem}
                          onClick={() => {
                            setCreateCaptain(emp);
                            setCreateCaptainSearch('');
                            setCreateCaptainResults([]);
                          }}
                        >
                          <EmployeeTaskStatsAvatar
                            employeeId={emp.id}
                            stats={employeeTaskStatsMap.get(Number(emp.id))}
                            fetchStats={false}
                            avatar={resolveImage(emp.avatar) || undefined}
                            size={24}
                            alt={emp.name}
                          />
                          {emp.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {isAdmin && scoreModalOpen && (
        <Modal
          title={`Очки: ${scoreMember?.name || ''}`}
          onClose={closeScoreEditor}
          onCancel={closeScoreEditor}
          cancelLabel="Отмена"
          cancelDisabled={scoreSaving}
          onSubmit={saveScoreEditor}
          submitLabel={scoreSaving ? 'Сохраняем...' : 'Сохранить'}
          submitDisabled={scoreLoading || scoreSaving}
          closeOnBackdrop={!scoreSaving}
          cardClassName={styles.scoreModalCard}
          bodyClassName={styles.scoreModalBody}
        >
          {scoreLoading ? (
            <div className={styles.state}>Загрузка категорий...</div>
          ) : scoreItems.length === 0 ? (
            <div className={styles.scoreGrid}>
              <div className={styles.emptyScoreState}>
                Категорий пока нет. Нажмите кнопку ниже, чтобы добавить первую категорию и сразу указать очки.
              </div>
              <button
                type="button"
                className={adminStyles.addOptionBtn}
                onClick={addScoreCategoryRow}
                disabled={categoryBusy || scoreLoading || scoreSaving}
              >
                + Добавить категорию
              </button>
            </div>
          ) : (
            <div className={styles.scoreGrid}>
              {scoreItems.map((item) => (
                <div key={item.clientId || item.categoryId} className={`${adminStyles.row} ${adminStyles.pollOptionRow} ${styles.scoreOptionRow}`}>
                  <div className={adminStyles.main}>
                    <div className={`${adminStyles.rowInline} ${styles.scoreRowInline}`}>
                      <Input
                        className={`${adminStyles.fieldInput} ${styles.scoreCategoryName}`}
                        hideClear={hideScoreInputClear}
                        value={item.categoryName}
                        onChange={(e) => {
                          const next = e.target.value;
                          setScoreItems((prev) => prev.map((x) => (
                            (x.clientId || x.categoryId) === (item.clientId || item.categoryId) ? { ...x, categoryName: next } : x
                          )));
                        }}
                        ariaLabel={`Категория ${item.categoryName || item.originalCategoryName || ''}`}
                      />
                      <Input
                        className={`${adminStyles.fieldInput} ${styles.scorePointsInput}`}
                        hideClear={hideScoreInputClear}
                        type="number"
                        value={item.points}
                        onChange={(e) => {
                          const next = e.target.value;
                          setScoreItems((prev) => prev.map((x) => (
                            (x.clientId || x.categoryId) === (item.clientId || item.categoryId) ? { ...x, points: next } : x
                          )));
                        }}
                        ariaLabel={`Очки категории ${item.categoryName}`}
                      />
                    </div>
                  </div>
                  <div className={`${adminStyles.actions} ${styles.scoreRowActions}`}>
                    <button
                      type="button"
                      className={`${adminStyles.iconBtn} ${adminStyles.optionDeleteBtn}`}
                      onClick={() => {
                        removeScoreCategoryRow(item);
                      }}
                      disabled={categoryBusy || scoreLoading || scoreSaving}
                      title="Удалить категорию"
                      aria-label="Удалить категорию"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M16 6V5.2C16 4.0799 16 3.51984 15.782 3.09202C15.5903 2.71569 15.2843 2.40973 14.908 2.21799C14.4802 2 13.9201 2 12.8 2H11.2C10.0799 2 9.51984 2 9.09202 2.21799C8.71569 2.40973 8.40973 2.71569 8.21799 3.09202C8 3.51984 8 4.0799 8 5.2V6M10 11.5V16.5M14 11.5V16.5M3 6H21M19 6V17.2C19 18.8802 19 19.7202 18.673 20.362C18.3854 20.9265 17.9265 21.3854 17.362 21.673C16.7202 22 15.8802 22 14.2 22H9.8C8.11984 22 7.27976 22 6.63803 21.673C6.07354 21.3854 5.6146 20.9265 5.32698 20.362C5 19.7202 5 18.8802 5 17.2V6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className={adminStyles.addOptionBtn}
                onClick={addScoreCategoryRow}
                disabled={categoryBusy || scoreLoading || scoreSaving}
              >
                + Добавить категорию
              </button>
              <div className={styles.scoreTotal}>
                {`Сумма очков: ${scoreItems.reduce((acc, x) => acc + (Number(x.points) || 0), 0)}`}
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
